import React, { useCallback, useMemo } from 'react';
import Tooltip from 'shared/components/Tooltip';
import PetAvatar from '../../../../../../Avatars/PetAvatar';
import PetParentAvatar from 'shared/components/Avatars/PetParentAvatar';
import { titleCase } from 'shared/utils';
import { IPetSearchResult } from '../../types/interfaces/IPetSearchResult';
import { VStack, HStack, Text, Box, Flex, Center, Checkbox } from '@televet/kibble-ui';
import sortBy from 'lodash-es/sortBy';
import LoadingSpinner from '../../../../../../LoadingSpinner';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useSidePanelSearch } from '../../../state/providers/SearchPanelProvider';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { getSubscriptionPaymentStatus } from '../../../components/Benefits/utils/careEnrollments';

interface PetListProps {
  petList: IPetSearchResult[];
  goToPet: (id: string) => void;
  goToPetParent: (id: string) => void;
  isGettingClinicPetParentsData: boolean;
  refetchSearchResults?: () => void;
}

const PetList = ({
  goToPet,
  goToPetParent,
  petList,
  isGettingClinicPetParentsData,
  refetchSearchResults,
}: PetListProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { isPatientIdSupported } = useIntegrationsProvider();
  const { isShowInactivePetsEnabled, setIsShowInactivePetsEnabled } = useSidePanelSearch();

  const isCareEnabled = useMemo(() => isFeatureEnabled(FeatureFlagName.CarePlans), [isFeatureEnabled]);

  const handleToggleShowInactivePets = useCallback(() => {
    if (!refetchSearchResults) return;

    setIsShowInactivePetsEnabled(!isShowInactivePetsEnabled);
    refetchSearchResults();
  }, [isShowInactivePetsEnabled, setIsShowInactivePetsEnabled, refetchSearchResults]);

  return (
    <Box className="PetResults_List" mt={6} mb={8}>
      <Flex align="center" justify="space-between" pb={2} px={4}>
        <Text fontWeight="bold">Patients</Text>
        <Box mt={1}>
          <Checkbox isChecked={isShowInactivePetsEnabled} onChange={handleToggleShowInactivePets} size="sm">
            Show Inactive
          </Checkbox>
        </Box>
      </Flex>
      {isGettingClinicPetParentsData && (!petList || petList.length === 0) && (
        <LoadingSpinner size="md" message="Loading Patients..." />
      )}

      {(!petList || petList.length === 0) && !isGettingClinicPetParentsData && (
        <Center color="text.subtle" py="3" fontSize="14">
          No results found
        </Center>
      )}
      {!isGettingClinicPetParentsData &&
        petList &&
        petList.length >= 1 &&
        sortBy(petList, (pet) => pet?.name?.toLowerCase()).map((pet, index: number) => {
          const { name, id, breed, gender, pimsId, petParents } = pet;
          const nPetParentAvatarsToDisplay = petParents.length === 2 ? 2 : 1;
          const careSubscriptionPaymentStatus = getSubscriptionPaymentStatus(pet.organizationPet);

          return (
            <Flex
              className="PetResults__Row"
              data-testid={`pet-list-item-${index}`}
              key={`${id} ${name} ${breed}`}
              py={3}
              px={4}
              onClick={(): void => goToPet(id)}
              justify="space-between"
              cursor="pointer"
              _hover={{
                bg: 'background.alpha.hover',
              }}
            >
              <Flex className="PetResults__Pet" justify="flex-start" flex="1 1 auto" align="center">
                <PetAvatar
                  className="PetResults__Pet-Avatar"
                  speciesType={pet.speciesType}
                  isDeceased={pet.computedIsDeceased}
                  isActive={pet.computedIsActive}
                  photoUrl={pet.photoUrl}
                  name={pet.name}
                  {...(isCareEnabled && { careStatus: careSubscriptionPaymentStatus })}
                />
                <VStack ml={2} align="left" spacing={0} className="PetResults__Pet-Details">
                  <Text fontWeight="bold">
                    {titleCase(name)}
                    {isPatientIdSupported && pimsId && <Text variant="subtle" size="xs">{` - ID: ${pimsId}`}</Text>}
                  </Text>
                  <Text isTruncated size="sm" variant="subtle">
                    {breed || 'Breed not recorded'}
                  </Text>
                  <Text isTruncated size="sm" variant="subtle">
                    {gender || 'Sex not recorded'}
                  </Text>
                </VStack>
              </Flex>
              <HStack className="PetResults__PetParent">
                {petParents.slice(0, nPetParentAvatarsToDisplay).map((clinicPetParent) => {
                  const { id } = clinicPetParent;
                  return (
                    <Box cursor="pointer" flex="1" key={id} onClick={(): void => goToPetParent(id)} textAlign="center">
                      <Flex justify="center" className="PetResults__PetParent-Avatar">
                        <PetParentAvatar clinicPetParent={clinicPetParent} size="lg" />
                      </Flex>
                    </Box>
                  );
                })}
                {petParents.length > nPetParentAvatarsToDisplay && (
                  <Tooltip
                    content={petParents
                      .slice(1)
                      .map(({ firstName, lastName }) => [firstName, lastName].join(' ').trim())
                      .join(', ')}
                  >
                    <Text flex="0 0 auto" size="xs" variant="subtle" fontWeight="bold">
                      {`+ ${petParents.length - nPetParentAvatarsToDisplay}`}
                    </Text>
                  </Tooltip>
                )}
              </HStack>
            </Flex>
          );
        })}
    </Box>
  );
};

export default PetList;
